import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  WORKBENCH_STATUSES,
  approvalValueFor,
  type WorkbenchStatus,
} from './workbench/statusScope';

export type StatusCounts = Record<WorkbenchStatus, number>;

/**
 * Die drei Reiterzahlen kommen aus EINEM Ladevorgang. Drei getrennt
 * angestossene Abfragen driften sichtbar gegeneinander, und eine Summe, die
 * nicht aufgeht, kostet die Glaubwuerdigkeit der ganzen Anzeige. Diese
 * Fehlerklasse hat die Werkbank schon einmal getroffen ("Produkt 1 von 0").
 *
 * Drei `head`-Counts in EINEM `Promise.all` statt einer gruppierten RPC: eine
 * Datenbankfunktion waere eine Migration, und die ist fuer diesen Umbau
 * ausgeschlossen. Entscheidend ist nicht die Zahl der Roundtrips, sondern dass
 * es genau ein Ergebnis gibt — entweder stehen alle drei Zahlen oder keine.
 *
 * Rueckgabe `null` heisst ausdruecklich "unbekannt", nicht "null Produkte" —
 * die Reiter zeigen dann keine Zahl statt einer falschen.
 */
export function useWorkbenchStatusCounts(feedId: string | null) {
  const query = useQuery({
    queryKey: ['workbench-status-counts', feedId],
    enabled: !!feedId,
    staleTime: 30_000,
    queryFn: async (): Promise<StatusCounts> => {
      const results = await Promise.all(
        WORKBENCH_STATUSES.map((status) =>
          supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('feed_id', feedId!)
            .eq('optimization_status', 'completed')
            .eq('approval_status', approvalValueFor(status)),
        ),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      return WORKBENCH_STATUSES.reduce((acc, status, i) => {
        acc[status] = results[i].count ?? 0;
        return acc;
      }, {} as StatusCounts);
    },
  });

  return { counts: query.data ?? null, isLoading: query.isLoading };
}
